import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { ArrowRight } from "lucide-react";
import GoogleButton from "@/components/GoogleButton";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      nav("/app");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F0F9FF] flex items-center justify-center p-6 sm:p-12">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 font-heading font-black text-xl mb-8">
          <span className="inline-block w-5 h-5 bg-[#3B82F6] border border-black" />
          Smart <span className="text-[#3B82F6]">S</span> Notes
        </Link>
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">/ Sign in</p>
        <h1 className="mt-2 font-heading font-black text-4xl sm:text-5xl tracking-tighter">Welcome back.</h1>
        <p className="mt-3 text-zinc-600">Your library is waiting.</p>

        <div className="mt-8">
          <GoogleButton label="Continue with Google" testId="login-google" />
        </div>

        <div className="my-6 flex items-center gap-3">
          <div className="flex-1 h-px bg-black/15" />
          <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">or with email</span>
          <div className="flex-1 h-px bg-black/15" />
        </div>

        <form onSubmit={submit} className="space-y-5" data-testid="login-form">
          <div>
            <label className="font-mono text-xs uppercase tracking-widest">Email</label>
            <input
              data-testid="login-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full mt-2 px-4 py-3 bg-white border-2 border-black focus:outline-none focus:ring-2 focus:ring-[#3B82F6] focus:ring-offset-2"
              autoComplete="email"
            />
          </div>
          <div>
            <label className="font-mono text-xs uppercase tracking-widest">Password</label>
            <input
              data-testid="login-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full mt-2 px-4 py-3 bg-white border-2 border-black focus:outline-none focus:ring-2 focus:ring-[#3B82F6] focus:ring-offset-2"
              autoComplete="current-password"
            />
          </div>
          {err && (
            <p data-testid="login-error" className="font-mono text-xs bg-[#FF3333] text-white px-3 py-2">
              {err}
            </p>
          )}
          <button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 font-mono text-sm uppercase tracking-widest px-6 py-4 bg-black text-white btn-brutal disabled:opacity-60"
          >
            {loading ? "Signing in…" : "Sign in"} <ArrowRight className="w-4 h-4" />
          </button>
        </form>

        <p className="mt-8 text-sm text-zinc-600">
          New here?{" "}
          <Link data-testid="link-register" to="/register" className="font-bold underline decoration-2 underline-offset-4 hover:text-[#FF3333]">
            Create an account
          </Link>
        </p>
      </div>
    </div>
  );
}
